import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, TextField, Typography,
  CircularProgress, Alert, InputAdornment, IconButton,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Button from '@mui/material/Button';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { resetPassword, clearAuthError } from '../reduxStore/AuthSlice';

const ResetPasswordPage: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { loading, error } = useSelector((state: RootState) => state.AuthSlice);

  const [newPwd,   setNewPwd]   = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [localErr, setLocalErr] = useState('');
  const [success,  setSuccess]  = useState(false);

  useEffect(() => {
    if (!token) navigate('/forgot-password', { replace: true });
  }, [token, navigate]);

  useEffect(() => {
    if (error) dispatch(clearAuthError());
    setLocalErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPwd, confirm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalErr('');

    if (!newPwd || !confirm) { setLocalErr('Tous les champs sont requis.'); return; }
    if (newPwd !== confirm)  { setLocalErr('Les mots de passe ne correspondent pas.'); return; }
    if (newPwd.length < 6)   { setLocalErr('Le mot de passe doit contenir au moins 6 caractères.'); return; }

    const result = await dispatch(resetPassword({ reset_token: token!, new_password: newPwd }));
    if (resetPassword.fulfilled.match(result)) {
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    }
  };

  const displayedError = localErr || error;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
      <Card sx={{ width: 420, boxShadow: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>

          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: 'warning.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
              <LockResetIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" fontWeight={700}>Nouveau mot de passe</Typography>
            <Typography variant="caption" color="text.secondary">Gsoft Web ERP</Typography>
          </Box>

          {success ? (
            <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
              Mot de passe mis à jour ! Redirection en cours…
            </Alert>
          ) : (
            <>
              {displayedError && <Alert severity="error" sx={{ mb: 2 }}>{displayedError}</Alert>}
              <form onSubmit={handleSubmit}>
                <TextField
                  label="Nouveau mot de passe"
                  type={showPwd ? 'text' : 'password'}
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  fullWidth size="small" sx={{ mb: 2 }}
                  autoComplete="new-password" autoFocus
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPwd((v) => !v)} edge="end">
                          {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Confirmer le mot de passe"
                  type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  fullWidth size="small" sx={{ mb: 3 }}
                  autoComplete="new-password"
                />
                <Button variant="contained" color="success" type="submit" fullWidth disabled={loading || !newPwd || !confirm}>
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Réinitialiser le mot de passe'}
                </Button>
              </form>
            </>
          )}

          <Box textAlign="center" mt={2}>
            <Typography variant="caption" color="text.secondary">
              <Link to="/login" style={{ color: 'inherit', fontWeight: 600 }}>Retour à la connexion</Link>
            </Typography>
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPasswordPage;
